/**
 * ProfileTemplateSelector.tsx - Profile Template Selector Component
 * 
 * CHANGE HISTORY:
 * - 251102_1706 danielbeer: Create ProfileTemplateSelector component
 * 
 * Select and customize profile templates
 */

import React, { useState } from 'react';
import type { ProfileTemplate } from '@opcentral/profylr';
import { PROFILE_TEMPLATES, createFromTemplate } from '@opcentral/profylr';

export interface ProfileTemplateSelectorProps {
  /** Callback when template is selected */
  onSelect: (template: ProfileTemplate) => void;
  
  /** Show template details */
  showDetails?: boolean;
  
  /** Custom className */
  className?: string;
}

/**
 * ProfileTemplateSelector - Browse and select profile templates
 * 
 * @example
 * ```tsx
 * <ProfileTemplateSelector
 *   onSelect={(template) => {
 *     const profile = await profylr.create(template);
 *   }}
 *   showDetails
 * />
 * ```
 */
export const ProfileTemplateSelector: React.FC<ProfileTemplateSelectorProps> = ({
  onSelect,
  showDetails = true,
  className = ''
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  
  const templates = Object.entries(PROFILE_TEMPLATES);
  
  const handleSelect = (templateName: string, template: ProfileTemplate) => {
    setSelectedTemplate(templateName);
    onSelect(template);
  };
  
  return (
    <div className={`profile-template-selector ${className}`}>
      <div className="profile-template-selector-header">
        <h2>Choose a Profile Template</h2>
        <p>Select a pre-configured profile template to get started quickly</p>
      </div>
      
      <div className="profile-template-selector-grid">
        {templates.map(([name, template]) => {
          const isSelected = selectedTemplate === name;
          
          return (
            <button
              key={name}
              className={`profile-template-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleSelect(name, template)}
            >
              <div className="profile-template-card-header">
                <h3>{template.name}</h3>
                {isSelected && (
                  <span className="profile-template-card-badge">Selected</span>
                )}
              </div>
              
              <p className="profile-template-card-description">
                {template.description}
              </p>
              
              {showDetails && (
                <div className="profile-template-card-details">
                  {/* Tags */}
                  {template.tags && template.tags.length > 0 && (
                    <div className="profile-template-card-tags">
                      {template.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="profile-template-tag">
                          {tag}
                        </span>
                      ))}
                      {template.tags.length > 3 && (
                        <span className="profile-template-tag-more">
                          +{template.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Key Features */}
                  <div className="profile-template-card-features">
                    {template.dataBoundaries?.datasets && (
                      <div className="profile-template-feature">
                        <span className="profile-template-feature-icon">📁</span>
                        <span>{template.dataBoundaries.datasets.length} datasets</span>
                      </div>
                    )}
                    {template.perspective?.tone && (
                      <div className="profile-template-feature">
                        <span className="profile-template-feature-icon">🎭</span>
                        <span>{template.perspective.tone}</span>
                      </div>
                    )}
                    {template.behavior?.llmModel && (
                      <div className="profile-template-feature">
                        <span className="profile-template-feature-icon">🤖</span>
                        <span>{template.behavior.llmModel}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProfileTemplateSelector;
